import React, { useCallback, useEffect, useState } from "react";
import { FileText, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { apiError, arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import EmptyState from "@/components/common/EmptyState";
import { TableSkeleton } from "@/components/common/Loading";
import Pagination from "@/components/common/Pagination";

const PAGE_LIMIT = 20;
const STATUS_OPTIONS = ["", "draft", "ai_generated", "pending_approval", "scheduled", "published", "failed"];

export default function Posts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async (p = 1, s = status) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_LIMIT };
      if (s) params.status = s;
      const { data } = await api.get("/posts", { params });
      setPosts(arr(data?.data));
      setPagination({ total: data?.total || 0, page: data?.page || 1, totalPages: data?.totalPages || 1 });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(1, status); }, [status]);

  const handlePage = (p) => { setPage(p); load(p, status); };

  const handleAction = async (id, action) => {
    try {
      if (action === "delete") { await api.delete(`/posts/${id}`); toast.success("Post deleted"); }
      else if (action === "publish") { await api.post(`/posts/${id}/publish-now`); toast.success("Published!"); }
      else if (action === "approve") { await api.post(`/posts/${id}/approve`); toast.success("Approved"); }
      else if (action === "cancel") { await api.post(`/posts/${id}/cancel`); toast.success("Cancelled"); }
      else if (action === "duplicate") { await api.post(`/posts/${id}/duplicate`); toast.success("Duplicated"); }
      else if (action === "retry") { await api.post(`/posts/${id}/retry`); toast.success("Queued for retry"); }
      load(page, status);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Posts"
        subtitle="Manage your Google Business Profile posts"
        icon={FileText}
        actions={
          <button onClick={() => navigate("/posts/create")} className="btn-primary flex items-center gap-1 text-sm">
            <Plus className="h-4 w-4" /> New Post
          </button>
        }
      />

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s || "all"}
            onClick={() => { setStatus(s); setPage(1); }}
            className={`rounded-xl border px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
              status === s ? "border-brand-600 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {s ? s.replace(/_/g, " ") : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <TableSkeleton />
      ) : posts.length === 0 ? (
        <EmptyState icon={FileText} title="No posts found" description="Create your first post or adjust your filters." />
      ) : (
        <div className="card-surface overflow-hidden p-0">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Scheduled</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {posts.map((p) => (
                  <tr key={p.id || p._id} className="text-sm hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-800 max-w-xs truncate">{p.title || "Untitled"}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{p.type}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-slate-500">
                      {p.scheduledAt ? new Date(p.scheduledAt).toLocaleString() : p.publishedAt ? new Date(p.publishedAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {p.status === "pending_approval" && (
                          <button onClick={() => handleAction(p.id || p._id, "approve")} className="text-xs text-green-600 hover:underline">Approve</button>
                        )}
                        {["scheduled", "approved"].includes(p.status) && (
                          <button onClick={() => handleAction(p.id || p._id, "publish")} className="text-xs text-brand-600 hover:underline">Publish</button>
                        )}
                        {p.status === "failed" && (
                          <button onClick={() => handleAction(p.id || p._id, "retry")} className="text-xs text-amber-600 hover:underline">Retry</button>
                        )}
                        {!["published", "cancelled"].includes(p.status) && (
                          <button onClick={() => handleAction(p.id || p._id, "cancel")} className="text-xs text-slate-500 hover:underline">Cancel</button>
                        )}
                        <button onClick={() => handleAction(p.id || p._id, "duplicate")} className="text-xs text-slate-500 hover:underline">Duplicate</button>
                        <button onClick={() => handleAction(p.id || p._id, "delete")} className="text-xs text-red-500 hover:underline">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-4">
            <Pagination page={pagination.page} totalPages={pagination.totalPages} total={pagination.total} limit={PAGE_LIMIT} onPage={handlePage} />
          </div>
        </div>
      )}
    </div>
  );
}
